import { useState } from "react";
import { FormControl, TextField, Typography, InputAdornment, Box, IconButton } from "@mui/material";
import { VisibilityOutlined, VisibilityOffOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from "@mui/material/styles";
import { myVJBlack900, myVJBlue200, myVJBlack200, myVJRed100 } from "../../constants/brandingColors";
import ExclamationError from "../Error/ExclamationError";

const FormControlBox = styled(FormControl)({
  display: "flex",
  flexFlow: "row",
});

const StyledInputBoxContainer = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(2)
}));

const TextContainer = styled("div")({
  alignSelf: "flex-start",
});


interface TextInputProps {
  fieldName: string;
  label: string;
  placeholder?: string;
  autoFocus?: boolean;
  onChange?: (event) => void;
  onClear?: (event) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  value?: string | number;
  error: any;
  isPercent?: boolean;
  type?: string;
  errorInPlainText?: boolean;
  ignoreErrorText?: boolean;
  id?: string;
  textSize?: "medium" | "small";
  dataTestId?: string;
  overrideAutoComplete?: boolean;
}

function TextInput({
  fieldName,
  label,
  onChange,
  onClear,
  value,
  isDisabled = false,
  isRequired = false,
  placeholder,
  autoFocus = false,
  error,
  type,
  errorInPlainText,
  ignoreErrorText,
  id,
  textSize,
  dataTestId,
  overrideAutoComplete = false,
}: TextInputProps) {

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  return (
    <>
      {label &&
        <TextContainer>
          <Typography variant="subtitle1" fontWeight="400">
            {label}
          </Typography>
        </TextContainer>
      }
      <StyledInputBoxContainer>
        <FormControlBox variant="outlined">
          <TextField
            required={isRequired ? true : false}
            type={!type || showPassword ? "text" : type}
            name={fieldName}
            variant="outlined"
            autoFocus={autoFocus}
            autoComplete={overrideAutoComplete ? "new-password" : fieldName} // Disable Chrome's aggressive usage of autocomplete
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            disabled={isDisabled}
            fullWidth={true}
            error={!!error}
            size={textSize}
            id={id}
            data-testid={dataTestId}
            helperText={
              !ignoreErrorText
                ? errorInPlainText
                  ? <ExclamationError errorText={error}></ExclamationError>
                  : error && <ExclamationError errorText={error}></ExclamationError>
                : ""
            }
            slotProps={{
              input: {
                endAdornment: type === 'password' ? (
                  <InputAdornment position="end" sx={{ gap: 2 }}>
                    {value && (
                      <IconButton
                        onClick={onClear}
                        edge="end"
                        aria-label="clear input"

                      >
                        <CloseIcon sx={{ fontSize: 20, color: myVJBlack900 }} />
                      </IconButton>
                    )}
                    <Box
                      className="input-divider"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        right: '47px',
                        width: '.5px',
                        backgroundColor: error ? myVJRed100 : myVJBlack200,
                        zIndex: 1,
                      }}
                    />
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? (
                        <VisibilityOffOutlined sx={{ fontSize: 24, color: myVJBlack900 }} />
                      ) : (
                        <VisibilityOutlined sx={{ fontSize: 24, color: myVJBlack900 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ) : (
                  value && (<InputAdornment position="end">
                    <IconButton
                      onClick={onClear}
                      edge="end"
                      aria-label="clear input"
                    >
                      <CloseIcon sx={{ fontSize: 20, color: myVJBlack900 }} />
                    </IconButton>
                  </InputAdornment>)
                )
              }
            }}
            sx={(theme) => ({
              height: 45,
              position: 'relative',
              paddingBottom: error ? theme.spacing(8) : 'none',
              '& .MuiInputBase-root': {
                height: 45,
                boxSizing: 'border-box',
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: myVJBlack200,
                },
                '&:hover fieldset': {
                  borderColor: error ? myVJRed100 : myVJBlack200,
                },
                '&.Mui-focused fieldset': {
                  borderColor: error ? myVJRed100 : myVJBlue200,
                },
                '&.Mui-focused .input-divider': {
                  backgroundColor: error ? myVJRed100 : myVJBlue200,
                  width: '2px',
                },
              },
              '& .MuiFormHelperText-root': {
                textAlign: 'left',
                marginLeft: 0,
              },
            })}
          />
        </FormControlBox>
      </StyledInputBoxContainer>
    </>
  );
}

TextInput.displayName = "TextInput";

export { TextInput };